import { useSyncExternalStore } from 'react';

/** The claim page open for an organization with the System of Record flag: what the SoR interceptor tells it. */
export interface SorClaimPage {
  sorName: string;
}

let claimPage: SorClaimPage | null = null;

/** Opens `page` to the SoR interceptor, until the returned function closes it. */
export const openSorClaimPage = (page: SorClaimPage): (() => void) => {
  claimPage = page;
  return () => {
    if (claimPage === page) {
      claimPage = null;
    }
  };
};

export const getSorClaimPage = (): SorClaimPage | null => claimPage;

// The claim writes the interceptor sent and has yet to hear back on, each with when it was sent.
const inFlight = new Map<object, number>();
let waitingSince: number | null = null;
const listeners = new Set<() => void>();

const update = () => {
  const earliest = inFlight.size > 0 ? Math.min(...inFlight.values()) : null;
  if (earliest !== waitingSince) {
    waitingSince = earliest;
    listeners.forEach((listener) => listener());
  }
};

/** Records that the request `key` stands for was sent now, and returns when. */
export const startClaimWrite = (key: object): number => {
  const sentAt = Date.now();
  inFlight.set(key, sentAt);
  update();
  return sentAt;
};

/** Records that the request `key` stands for was answered, and returns when it was sent, or null if untracked. */
export const endClaimWrite = (key: object): number | null => {
  const sentAt = inFlight.get(key);
  if (sentAt === undefined) {
    return null;
  }
  inFlight.delete(key);
  update();
  return sentAt;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getWaitingSince = () => waitingSince;

/** When the earliest claim write still waiting on its answer was sent, or null while none waits. */
export const useSorWaitingSince = (): number | null => useSyncExternalStore(subscribe, getWaitingSince);
